<template>
  <div class="rrd-db-page">
    <!-- STICKY TOP: TITLE + FILTERS + ACTIONS -->
    <div class="rrd-sticky-top">
      <div class="rrd-sticky-head-row px-2 mb-2">
        <div class="rrd-head-slot rrd-head-slot--left" aria-hidden="true"></div>
        <h3 class="rrd-title m-0 rrd-head-title">Database of Qualifications</h3>
        <div class="rrd-head-slot rrd-head-slot--right" aria-hidden="true"></div>
      </div>
      <div class="rrd-filter-card">
        <!-- Mobile toggle -->
        <div class="d-flex d-md-none justify-content-between align-items-center mb-2">
          <div class="rrd-card-title">Filters</div>
          <button class="btn rrd-btn-ghost rrd-btn-icon" type="button" @click="filtersOpen = !filtersOpen">
            <i class="fa" :class="filtersOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
          </button>
        </div>

        <!-- COLLAPSE WRAP (mobile), always open on md+ -->
        <div :class="['rrd-filters-collapse', { 'is-open': filtersOpen }]">
          <div class="row">
            <!-- Origin -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3 mb-3">
              <label class="rrd-filter-label">Origin (economy) of the awarded qualification</label>
              <select v-model="filter.country_origin" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option v-for="c in originOptions" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>

            <!-- Title -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Title of the qualification</label>
              <input
                v-model.trim="filter.name_of_foreign_qualification"
                type="text"
                class="form-control rrd-filter-input"
                placeholder="e.g. Bachelor of Arts"
              />
            </div>

            <!-- Institution -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Name of awarding institution</label>
              <input
                v-model.trim="filter.name_awarding_institution_original_lang"
                type="text"
                class="form-control rrd-filter-input"
                placeholder="e.g. Simon Fraser University"
              />
            </div>

            <!-- Field -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Main field of study</label>
              <select v-model="filter.qualification_area" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option v-for="f in fieldOptions" :key="`${f.id}_field`" :value="f.id">
                  {{ f.name }}
                </option>
              </select>
            </div>

            <!-- Length -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Official length of programme</label>
              <input
                v-model.trim="filter.length_of_study"
                type="number"
                class="form-control rrd-filter-input"
                placeholder="e.g. 4"
              />
            </div>

            <!-- Access requirement -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Access requirement</label>
              <select v-model="filter.enrollment_requirements" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option v-for="a in accessRequirementOptions" :key="a.value" :value="a.value">
                  {{ a.text }}
                </option>
              </select>
            </div>

            <!-- NQF -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Level in the home country (NQF if exists)</label>
              <select v-model="filter.level_qualification_country_origin" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option v-for="n in nqfOptions" :key="`${n.value}_nqf`" :value="n.value">
                  {{ n.text }}
                </option>
              </select>
            </div>

            <!-- EQF -->
            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Level of the EQF</label>
              <select v-model="filter.eqf" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option v-for="e in eqfOptions" :key="`${e.value}_eqf`" :value="e.value">
                  {{ e.text }}
                </option>
              </select>
            </div>

            <!-- Further study -->
<!--            <div class="col-12 col-md-6 col-lg-4 col-xl-3">
              <label class="rrd-filter-label">Access to further study in the country of origin</label>
              <select v-model="filter.further_study" class="form-control rrd-filter-input">
                <option :value="null">All</option>
                <option :value="1">Yes</option>
                <option :value="0">No</option>
              </select>
            </div>-->

            <!-- ACTIONS (after filters) -->
            <div class="col-12 mt-3">
              <div class="rrd-actions">
                <button class="btn rrd-btn-ghost" :disabled="isLoading" @click="onReset">
                  <i class="fa fa-undo mr-1"></i> Reset
                </button>

                <button class="btn rrd-filter-btn" :disabled="isLoading" @click="onSearch">
                  <template v-if="isLoading">
                    <i class="fa fa-spinner fa-spin mr-1"></i> Loading
                  </template>
                  <template v-else>
                    <i class="fa fa-search mr-1"></i> Search
                  </template>
                </button>

                <div class="ml-auto d-none d-md-flex align-items-center">
                  <span class="rrd-caption mr-2">Rows:</span>
                  <select
                    v-model.number="perPage"
                    class="form-control rrd-filter-input rrd-mini-select"
                    :disabled="isLoading"
                    @change="onSearch"
                  >
                    <option v-for="l in [10, 20, 50]" :key="l" :value="l">{{ l }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- quick chips -->
          <div class="rrd-active-filters mt-2" v-if="activeFilters.length">
            <span class="rrd-caption mr-2">Active:</span>

            <button
              v-for="(chip, i) in activeFilters"
              :key="`${chip.key}_${i}`"
              type="button"
              class="rrd-pill rrd-pill-soft rrd-chip-btn"
              @click="removeFilterChip(chip)"
              :title="`Remove ${chip.label}`"
            >
              <span class="rrd-chip-text">{{ chip.label }}: {{ chip.text }}</span>
              <span class="rrd-chip-x">×</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SCROLL BODY -->
    <div class="rrd-scroll-body">
      <div class="rrd-card">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="rrd-card-title">Qualifications</div>
          <div class="rrd-caption">Showing {{ results.length }} of {{ totalRows }}</div>
        </div>

        <div class="rrd-table-wrap">
          <b-table
            :items="results"
            :fields="fields"
            :busy="isLoading"
            responsive
            striped
            hover
            small
            class="rrd-btable"
            show-empty
            empty-text="No results found for selected filters."
          >
            <template #table-busy>
              <div class="rrd-empty">
                <i class="fa fa-spinner fa-spin mr-2"></i> Loading results...
              </div>
            </template>

            <template #cell(country.name)="data">
              <span class="rrd-pill">{{ data.value || '-' }}</span>
            </template>

            <template #cell(level_qualification_country_origin)="data">
              <span class="rrd-pill rrd-pill-soft">{{ data.value || '-' }}</span>
            </template>
            <template #cell(eqf_levels)="data">
              <span class="rrd-pill rrd-pill-soft">{{ data.value || '-' }}</span>
            </template>
            <template #cell(further_study)="data">
              <span class="rrd-pill rrd-pill-soft">{{ data.value == '1' ? 'Yes' : 'No' }}</span>
            </template>
            <template #cell(recognition_results)="data">
                              <RecognitionDecisionCell :results="data.value"/>
            </template>

          </b-table>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-pagination
            v-show="totalRows > perPage"
            class="app_pagination"
            size="sm"
            :disabled="isLoading"
            :hide-goto-end-buttons="true"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="updatePagination"
          >
            <template #prev-text>
              <i class="fa fa-angle-left" aria-hidden="true"></i>
            </template>
            <template #next-text>
              <i class="fa fa-angle-right" aria-hidden="true"></i>
            </template>

          </b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCountryList, getDatabaseOfQualification, getQualificationList } from "@/services/public";
import RecognitionBadge from "@/components/statistic/RecognitionBadge.vue";
import RecognitionList from "@/components/statistic/RecognitionList.vue";
import RecognitionDecisionCell from "@/components/statistic/RecognitionDecisionCell.vue";

export default {
  name: "DatabaseOfQualifications",
  components: {RecognitionDecisionCell, RecognitionList, RecognitionBadge},
  data() {
    return {
      isLoading: false,
      filtersOpen: false,

      originOptions: [],
      fieldOptions: [],
      accessRequirementOptions: [
        { value: null, text: "All" },
        { value: "1", text: "Secondary Schooll Diploma" },
        { value: "2", text: "Short Cycle" },
        { value: "3", text: "First Cycle" },
        { value: "4", text: "Second Cycle" },
        { value: "5", text: "Pre-graduate Degree" },
        { value: "6", text: "Graduate Degree" },
        { value: "7", text: "Other" },
      ],
      nqfOptions: [
        { value: 1, text: "1" },
        { value: 2, text: "2" },
        { value: 3, text: "3" },
        { value: 4, text: "4" },
        { value: 5, text: "5" },
        { value: 6, text: "6" },
        { value: 6.1, text: "6.1" },
        { value: 6.2, text: "6.2" },
        { value: 7, text: "7" },
        { value: 7.1, text: "7.1" },
        { value: 7.2, text: "7.2" },
        { value: 8, text: "8" },
        { value: 9, text: "9" },
        { value: 10, text: "10" },
        { value: 11, text: "11" },
        { value: 12, text: "12" },
      ],
      eqfOptions: [
        { value: 1, text: "1" },
        { value: 2, text: "2" },
        { value: 3, text: "3" },
        { value: 4, text: "4" },
        { value: 5, text: "5" },
        { value: 6, text: "6" },
        { value: 7, text: "7" },
        { value: 8, text: "8" },
      ],

      filter: {
        country_origin: null,
        name_of_foreign_qualification: "",
        name_awarding_institution_original_lang: "",
        qualification_area: null,
        length_of_study: "",
        enrollment_requirements: null,
        level_qualification_country_origin: null,
        eqf: null,
        further_study: null,
      },

      fields: [
        { key: "country.name", label: "Origin (economy) of the awarded qualification", thClass: "rrd-th", tdClass: "rrd-td" },
        { key: "name_of_foreign_qualification", label: "Title of the Qualification", thClass: "rrd-th", tdClass: "rrd-td" },
        { key: "name_awarding_institution_original_lang", label: "Name of awarding institution", thClass: "rrd-th", tdClass: "rrd-td" },
        { key: "main_field_of_study.name", label: "Main field of study", thClass: "rrd-th d-none d-lg-table-cell", tdClass: "rrd-td d-none d-lg-table-cell" },
        { key: "length_of_study", label: "Official length of programme", thClass: "rrd-th d-none d-xl-table-cell", tdClass: "rrd-td d-none d-xl-table-cell" },
        { key: "enrollment_requirements", label: "Access requirement", thClass: "rrd-th d-none d-xl-table-cell", tdClass: "rrd-td d-none d-xl-table-cell" },
        { key: "level_qualification_country_origin", label: "NQF level", thClass: "rrd-th text-center", tdClass: "rrd-td text-center" },
        { key: "eqf_levels", label: "EQF level", thClass: "rrd-th text-center", tdClass: "rrd-td text-center" },
        { key: "further_study", label: "Access to further study in the country of origin:", thClass: "rrd-th text-center", tdClass: "rrd-td text-center" },
        { key: "recognition_results", label: "Recognition in economy", thClass: "rrd-th text-center", tdClass: "rrd-td text-center" },
      ],

      results: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
    };
  },

  computed: {
    activeFilters() {
      const f = this.filter;
      const chips = [];

      const findById = (arr, id) => (arr || []).find((x) => String(x.id) === String(id));
      const findByValue = (arr, val) => (arr || []).find((x) => String(x.value) === String(val));

      if (f.country_origin) {
        const c = findById(this.originOptions, f.country_origin);
        chips.push({ key: "country_origin", label: "Origin", value: f.country_origin, text: c ? c.name : String(f.country_origin) });
      }
      if (f.name_of_foreign_qualification) {
        chips.push({ key: "name_of_foreign_qualification", label: "Title", value: f.name_of_foreign_qualification, text: f.name_of_foreign_qualification });
      }
      if (f.name_awarding_institution_original_lang) {
        chips.push({ key: "name_awarding_institution_original_lang", label: "Institution", value: f.name_awarding_institution_original_lang, text: f.name_awarding_institution_original_lang });
      }
      if (f.qualification_area) {
        const area = findById(this.fieldOptions, f.qualification_area);
        chips.push({ key: "qualification_area", label: "Field", value: f.qualification_area, text: area ? area.name : String(f.qualification_area) });
      }
      if (f.length_of_study) {
        chips.push({ key: "length_of_study", label: "Length", value: f.length_of_study, text: String(f.length_of_study) });
      }
      if (f.enrollment_requirements) {
        const ar = findByValue(this.accessRequirementOptions, f.enrollment_requirements);
        chips.push({ key: "enrollment_requirements", label: "Access req", value: f.enrollment_requirements, text: ar ? ar.text : String(f.enrollment_requirements) });
      }
      if (f.level_qualification_country_origin !== null && f.level_qualification_country_origin !== undefined) {
        chips.push({ key: "level_qualification_country_origin", label: "NQF", value: f.level_qualification_country_origin, text: String(f.level_qualification_country_origin) });
      }
      if (f.eqf !== null && f.eqf !== undefined) {
        chips.push({ key: "eqf", label: "EQF", value: f.eqf, text: String(f.eqf) });
      }
      if (f.further_study === 1) chips.push({ key: "further_study", label: "Further study", value: 1, text: "Yes" });
      if (f.further_study === 0) chips.push({ key: "further_study", label: "Further study", value: 0, text: "No" });

      return chips;
    },
  },

  methods: {
    async onSearch() {
      this.currentPage = 1;
      await this.fetchData();
    },

    async updatePagination() {
      await this.fetchData();
    },

    removeFilterChip(chip) {
      const key = chip.key;

      if (key === "country_origin" || key === "qualification_area") this.filter[key] = null;
      else if (key === "enrollment_requirements") this.filter[key] = null;
      else if (key === "level_qualification_country_origin" || key === "eqf") this.filter[key] = null;
      else if (key === "further_study") this.filter[key] = null;
      else this.filter[key] = "";

      this.onSearch();
    },

    async fetchData() {
      this.isLoading = true;
      try {
        const payload = {
          ...this.filter,
          page: this.currentPage,
          limit: this.perPage,
        };

        const res = await getDatabaseOfQualification(payload);
        const items = res.data.items;

        this.results = items.map(this.normalizeRow);
        this.totalRows = Number(res.data.total || 0);
      } catch (e) {
        this.results = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchFilterData() {
      try {
        const qualificationRes = await getQualificationList({});
        const countryRes = await getCountryList({ limit: 1000 });
        this.fieldOptions = qualificationRes.data;
        this.originOptions = countryRes.data;
      } catch (e) {
        this.originOptions = [];
        this.fieldOptions = [];
      }
    },

    onReset() {
      this.filter = {
        country_origin: null,
        name_of_foreign_qualification: "",
        name_awarding_institution_original_lang: "",
        qualification_area: null,
        length_of_study: "",
        enrollment_requirements: null,
        level_qualification_country_origin: null,
        eqf: null,
        further_study: null,
      };
      this.currentPage = 1;
      this.fetchData();
    },

    normalizeRow(row) {
      const safe = row || {};
      return {
        ...safe,
        country: safe.country || { name: "" },
        main_field_of_study: safe.main_field_of_study || { name: "" },
      };
    },
  },

  async mounted() {
    await this.fetchData();
    this.fetchFilterData();
    const doc = document.documentElement;
    const over = [];
    document.querySelectorAll('body *').forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.right > doc.clientWidth + 1) over.push({ el, right: r.right, w: r.width, cls: el.className });
    });
    over.sort((a,b)=>b.right-a.right);
    console.table(over.slice(0, 10));
  },
};
</script>
